import type { OutlineState } from "../outline/controller";

/**
 * Строка режима — визитная карточка этого интерфейса.
 *
 * Модальный ввод выигрывает тем, что в навигации свободен весь алфавит; цена — человек
 * обязан ЗНАТЬ, в каком он режиме и какую первую букву аккорда уже нажал. Обычно это
 * держат в голове (и промахиваются). Здесь и режим, и накопленный буфер аккорда видны
 * постоянно, а рядом — подсказка, чем этот префикс может закончиться.
 */
export function ModeBar({ state, hints }: { state: OutlineState; hints: string[] }) {
	return (
		<div className="flex h-8 shrink-0 items-center gap-4 rounded-b-[var(--panel-radius)] border-t border-stroke-soft bg-panel-dim px-3 text-xs font-light text-ink-muted">
			<span
				className={`shrink-0 rounded-sm px-2 py-px ${
					state.mode === "edit" ? "bg-link font-medium text-white" : "bg-active text-ink"
				}`}
			>
				{state.mode === "edit" ? "правка" : "навигация"}
			</span>

			{state.chord !== "" && (
				<span className="flex min-w-0 items-baseline gap-2">
					<span className="font-mono text-link">{state.chord}</span>
					<span className="truncate">→ {hints.join(" · ") || "нет продолжения"}</span>
				</span>
			)}

			{state.selectedKeys.size > 1 && (
				<span className="shrink-0 text-ink">выделено {state.selectedKeys.size}</span>
			)}

			<span className="ml-auto flex shrink-0 items-center gap-3">
				<span>
					<span className="kbd">aa</span> команды
				</span>
				<span>
					<span className="kbd">↵</span> правка
				</span>
				<span>
					<span className="kbd">/</span> поиск
				</span>
			</span>
		</div>
	);
}

/** Чем может закончиться уже набранный префикс. */
export function chordHints(chord: string): string[] {
	if (chord === "") {
		return [];
	}
	const all: Record<string, string> = {
		ee: "правка",
		ei: "правка с начала",
		ea: "правка с конца",
		ec: "свернуть до уровня 0–9",
		ss: "статус",
		st: "мультивыбор",
		sd: "детали",
		sn: "показать заметки",
		rt: "→ задача",
		rp: "→ проект",
		rn: "→ текст",
		rf: "→ фокус",
		dd: "срок",
		dr: "повторение",
		td: "срок сегодня",
		tm: "срок завтра",
		tt: "теги",
		cd: "снять срок",
		ct: "снять теги",
		cn: "снять заметку",
		cp: "снять вплетение",
		nn: "заметка",
		pp: "пин",
		pl: "вплести",
		ar: "архив / вернуть",
		mm: "переместить",
		ff: "поиск",
		uu: "отменить",
		aa: "палитра команд",
		ii: "инспектор",
		gt: "сегодня",
		gi: "входящие",
		gp: "проекты",
		gc: "календарь",
		gr: "повторяющиеся",
		gd: "просрочено",
		ga: "архив",
		gf: "пины",
		go: "фокус"
	};
	return Object.entries(all)
		.filter(([key]) => key.startsWith(chord) && key !== chord)
		.map(([key, label]) => `${key} ${label}`)
		.slice(0, 6);
}
